// GENERATED FILE — do not edit.
// Synced from api/src/zero/schema.ts via `bun run sync:zero`.
// Edit the source there, then re-run the sync.

// Zero sync schema for the chat feature. Hand-written to mirror the relevant
// columns of the Drizzle tables in ../db/schema.ts (camelCase here → snake_case
// Postgres via `.from(...)`). Only the columns Zero needs to sync/mutate are
// declared; undeclared columns (e.g. tool fields) are simply not synced.
//
// NOTE: Postgres `timestamp` columns are represented as Zero `number()` (epoch
// ms). The number↔timestamp conversion is handled by the server adapter — verify
// end-to-end once the client/zero-cache path is wired.

import {
    boolean,
    createBuilder,
    createSchema,
    enumeration,
    json,
    number,
    type ReadonlyJSONObject,
    relationships,
    string,
    table,
} from '@rocicorp/zero';

const user = table('user')
    .columns({
        id: string(),
        name: string(),
        email: string(),
        image: string().optional(),
    })
    .primaryKey('id');

const chat = table('chat')
    .columns({
        id: string(),
        userId: string().from('user_id'),
        title: string(),
        starred: boolean().optional(),
        isDeleted: boolean().from('is_deleted').optional(),
        lastMessageAt: number().from('last_message_at').optional(),
        createdAt: number().from('created_at').optional(),
        updatedAt: number().from('updated_at').optional(),
    })
    .primaryKey('id');

const chatMessage = table('chatMessage')
    .from('chat_message')
    .columns({
        id: string(),
        chatId: string().from('chat_id'),
        userId: string().from('user_id').optional(),
        clientMessageId: string().from('client_message_id').optional(),
        type: enumeration<'user' | 'assistant' | 'system' | 'tool'>(),
        status: enumeration<
            | 'pending'
            | 'streaming'
            | 'awaiting_approval'
            | 'approved'
            | 'rejected'
            | 'executing'
            | 'completed'
            | 'failed'
            | 'cancelled'
        >(),
        // Free-form JSON; user messages use `{ content: string }`.
        message: json<ReadonlyJSONObject>(),
        createdAt: number().from('created_at').optional(),
        updatedAt: number().from('updated_at').optional(),
        completedAt: number().from('completed_at').optional(),
        isDeleted: boolean().from('is_deleted').optional(),
    })
    .primaryKey('id');

// SECURITY: declare only safe columns — the encrypted `credential` column is
// intentionally NOT included so it can never sync to clients. `tags`/`sharedWith`
// (pg arrays) are omitted for now; the sidebar defaults the tag.
const host = table('host')
    .columns({
        id: string(),
        userId: string().from('user_id'),
        name: string(),
        hostname: string(),
        port: number(),
        sshUser: string().from('ssh_user'),
        authMethod: enumeration<'key' | 'password' | 'agent'>().from(
            'auth_method',
        ),
        os: string().optional(),
        note: string().optional(),
        // Desktop transport policy (relay/direct/auto) — safe metadata; the
        // credential itself stays excluded (see SECURITY note above).
        connectionMode: enumeration<'relay' | 'direct' | 'auto'>().from(
            'connection_mode',
        ),
        isDeleted: boolean().from('is_deleted').optional(),
        lastSeenAt: number().from('last_seen_at').optional(),
        createdAt: number().from('created_at').optional(),
        updatedAt: number().from('updated_at').optional(),
    })
    .primaryKey('id');

const terminalWorkspace = table('terminalWorkspace')
    .from('terminal_workspace')
    .columns({
        userId: string().from('user_id'),
        // The serialized tabs + split-pane tree; opaque JSON to the server.
        layout: json<ReadonlyJSONObject>(),
        updatedAt: number().from('updated_at').optional(),
    })
    .primaryKey('userId');

const hostRelationships = relationships(host, ({ one }) => ({
    user: one({ sourceField: ['userId'], destField: ['id'], destSchema: user }),
}));

const chatRelationships = relationships(chat, ({ one, many }) => ({
    user: one({ sourceField: ['userId'], destField: ['id'], destSchema: user }),
    messages: many({
        sourceField: ['id'],
        destField: ['chatId'],
        destSchema: chatMessage,
    }),
}));

const chatMessageRelationships = relationships(chatMessage, ({ one }) => ({
    chat: one({ sourceField: ['chatId'], destField: ['id'], destSchema: chat }),
}));

export const schema = createSchema({
    tables: [user, host, terminalWorkspace, chat, chatMessage],
    relationships: [
        hostRelationships,
        chatRelationships,
        chatMessageRelationships,
    ],
});

export type Schema = typeof schema;

/** ZQL query builder — `zql.chat.where('id', id).one()`, etc. */
export const zql = createBuilder(schema);

/** JWT claims the permission rules read (`sub` = userID). */
export type ZeroAuthData = { sub: string };

/** Context threaded into mutators on both client and server. */
export type ZeroContext = { userID: string };

// Register our schema + mutator context so `tx.mutate.*` and `ctx` are typed.
declare module '@rocicorp/zero' {
    interface DefaultTypes {
        schema: typeof schema;
        context: ZeroContext;
    }
}
